import React, { useState, useRef } from "react";
import { Link, Tooltip, useMediaQuery, Checkbox, Button } from "@mui/material";
import {
  styles,
  getScoreColor,
  getDetailedRowColor,
  getDomainAvailabilityColor,
  columnDescriptions,
} from "./DataTable.styles";
import TrademarkReportSummary from "./TrademarkReportSummary";
import DomainNameReportSummary from "./DomainNameReportSummary";
import DetailedTrademarkReport, {
  detailedColumns,
} from "./DetailedTrademarkReport";
import DomainUtilityReport from "./DomainUtilityReport";
import BrandNameScoresReport from "./BrandNameScoresReport";
import FullReportSummary from "./FullReportSummary";
import { useEffect } from "react";
import { FONT_SIZES } from "../../constants/fontSizes";
import PrintIcon from "@mui/icons-material/Print";
import colors from "../../constants/colors";
import { styles as inputFormStyles } from "../user_input/UserInputForm.styles";
import SocialMediaUserNameAvailability from "./SocialMediaUserNameAvailability";

const tooltipStyles = {
  tooltip: {
    sx: {
      fontSize: FONT_SIZES.TEXT_HINT.xs,
      "@media (min-width: 600px)": {
        fontSize: FONT_SIZES.TEXT_HINT.sm,
      },
    },
  },
};

const numericWidth = "8%";

// Column definitions
//  for each table
const linkedReportColumns = [
  {
    id: "Name Candidate",
    label: "Candidate Name",
    width: "15%",
  },
  {
    id: "score",
    label: "Overall Score",
    width: numericWidth,
    tooltip: columnDescriptions["Overall Score"],
    tooltipProps: tooltipStyles,
    format: (value) => parseFloat(value) || 0,
    getColor: (value) => getScoreColor(parseFloat(value) || 0),
  },
  {
    id: "Name Similarity",
    label: "Name Similarity",
    width: numericWidth,
    tooltip: columnDescriptions["Name Similarity"],
    tooltipProps: tooltipStyles,
    format: (value) => parseFloat(value) || 0,
    getColor: (value) => getScoreColor(parseFloat(value) || 0),
  },
  {
    id: "Industry Similarity",
    label: "Industry Similarity",
    width: numericWidth,
    tooltip: columnDescriptions["Industry Similarity"],
    tooltipProps: tooltipStyles,
    format: (value) => parseFloat(value) || 0,
    getColor: (value) => getScoreColor(parseFloat(value) || 0),
  },
  {
    id: "Legal Activity",
    label: "Legal Activity",
    width: numericWidth,
    tooltip: columnDescriptions["Legal Activity"],
    tooltipProps: tooltipStyles,
    format: (value) => parseFloat(value) || 0,
    getColor: (value) => getScoreColor(parseFloat(value) || 0),
  },
  {
    id: "Existing Trademarks",
    label: "Existing Trademarks",
    width: "15%",
    tooltip: columnDescriptions["Existing Trademarks"],
    tooltipProps: tooltipStyles,
    format: (value) => {
      if (!Array.isArray(value) || value.length === 0)
        return "No Matching Trademark Found (that's great!)";
      return value.map((link, index) => (
        <React.Fragment key={index}>
          {index > 0 && ", "}
          <Link
            href={link.match(/\((.*?)\)/)[1]}
            target="_blank"
            rel="noopener noreferrer"
          >
            {link.match(/\[(.*?)\]/)[1]}
          </Link>
        </React.Fragment>
      ));
    },
  },
];

const domainSummaryColumns = [
  { id: "name", label: "Candidate Name", width: "20%" },
  {
    id: "domain_availability_score",
    label: "Domain Availability Score",
    width: "8%",
    tooltip:
      "Score indicating how available the domain name is (1-5 scale, higher means more available)",
    tooltipProps: tooltipStyles,
    format: (value) => parseFloat(value) || 0,
    getColor: (value) => getDomainAvailabilityColor(parseFloat(value) || 0),
  },
  {
    id: "exact_matches",
    label: "Exact Domain Matches",
    tooltip: "Exact domain name matches found",
    tooltipProps: tooltipStyles,
    width: "15%",
    format: (value) => {
      if (!value) return "";
      const domains = value.split(", ");
      return (
        <Tooltip
          title={value}
          placement="top"
          arrow
          componentsProps={tooltipStyles}
        >
          <span style={styles.tooltipText}>
            {domains.length > 5 ? (
              <>
                {domains.slice(0, 5).map((domain, i) => (
                  <React.Fragment key={domain}>
                    {i > 0 && ", "}
                    <Link
                      href={`https://www.godaddy.com/domainsearch/find?domainToCheck=${domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {domain}
                    </Link>
                  </React.Fragment>
                ))}
                ...
              </>
            ) : (
              domains.map((domain, i) => (
                <React.Fragment key={domain}>
                  {i > 0 && ", "}
                  <Link
                    href={`https://www.godaddy.com/domainsearch/find?domainToCheck=${domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {domain}
                  </Link>
                </React.Fragment>
              ))
            )}
          </span>
        </Tooltip>
      );
    },
  },
  {
    id: "min_price",
    label: "Minimum Price",
    tooltip: "Minimum price for available domains",
    width: "10%",
    tooltipProps: tooltipStyles,
    format: (value) => (value ? `$${parseFloat(value).toFixed(2)}` : ""),
  },
  {
    id: "max_price",
    label: "Maximum Price",
    width: "10%",
    tooltip: "Maximum price for available domains",
    tooltipProps: tooltipStyles,
    format: (value) => (value ? `$${parseFloat(value).toFixed(2)}` : ""),
  },
  {
    id: "available_domains",
    label: "All Suggested Domains",
    tooltip: "List of all suggested available domains",
    width: "15%",
    tooltipProps: tooltipStyles,
    format: (value) => {
      if (!value) return "";
      const domains = value.split(", ").filter(Boolean);
      const uniqueDomains = [...new Set(domains)];
      const godaddyRoot =
        "https://www.godaddy.com/domainsearch/find?domainToCheck=";
      const godaddyLink = `${godaddyRoot}${uniqueDomains.join("%2C+")}`;

      // Truncated version for screen
      const truncated = (
        <Tooltip
          title={value}
          placement="top"
          arrow
          componentsProps={tooltipStyles}
        >
          <span style={styles.tooltipText}>
            <Link
              href={godaddyLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginRight: "8px" }}
            >
              Bulk Search on GoDaddy
            </Link>
            <br />
            {uniqueDomains.slice(0, 5).map((domain, index) => (
              <React.Fragment key={`${domain}-${index}`}>
                {index > 0 && <br />}
                <Link
                  href={`${godaddyRoot}${domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {domain}
                </Link>
              </React.Fragment>
            ))}
            {uniqueDomains.length > 5 && "..."}
          </span>
        </Tooltip>
      );

      // Full version for print
      const full = (
        <span>
          {uniqueDomains.map((domain, i) => (
            <React.Fragment key={`${domain}-${i}`}>
              {i > 0 && ", "}
              {domain}
            </React.Fragment>
          ))}
        </span>
      );

      return (
        <>
          <span className="all-domains-truncate">{truncated}</span>
          <span className="all-domains-full">{full}</span>
        </>
      );
    },
  },
];

const ResultsTables = ({
  results,
  onCopyNames,
  jobType,
  handleAddToCandidates,
  handleAddAllCandidates,
  formData,
  setDrawerOpen,
  setMobileMenuAnchorEl,
}) => {
  const isMobile = useMediaQuery(
    `(max-width:${styles.reportViewWidth.limitMobileWidth})`
  );
  const flipBrandNameCols = useMediaQuery(`(max-width:4000px)`);
  const flipDomainSummaryCols = useMediaQuery(`(max-width:1300px)`);
  const flipDomainUtilityCols = useMediaQuery(`(max-width:1300px)`);
  const flipFullReportCols = useMediaQuery(`(max-width:1300px)`);
  const flipTrademarkReportCols = useMediaQuery(`(max-width:1300px)`);

  const isPortrait = useMediaQuery("(orientation:portrait)");
  const showDetailedReport = !isMobile || !isPortrait;
  const showSummaryReport = !useMediaQuery(
    `(max-width:${styles.reportViewWidth.limitSummaryWidth})`
  );
  const showDomainReport = !useMediaQuery(
    `(max-width:${styles.reportViewWidth.limitDomainWidth})`
  );
  const showBrandNameScoresReport = !useMediaQuery(
    `(max-width:${styles.reportViewWidth.limitDomainWidth})`
  );

  const isMobileView = isMobile && isPortrait;
  const [selectedTrademarkRows, setSelectedTrademarkRows] = useState(new Set());
  const [selectedDomainRows, setSelectedDomainRows] = useState(new Set());
  const [domainReportExpanded, setDomainReportExpanded] = useState(true);
  const [domainUtilityExpanded, setDomainUtilityExpanded] = useState(true);
  const [brandNameScoresExpanded, setBrandNameScoresExpanded] = useState(true);
  const [trademarkReportExpanded, setTrademarkReportExpanded] = useState(true);
  const showUtilityReport = !useMediaQuery(
    `(max-width:${styles.reportViewWidth.limitDomainWidth})`
  );
  const [fullReportExpanded, setFullReportExpanded] = useState(true);
  const showFullReport = !useMediaQuery(
    `(max-width:${styles.reportViewWidth.limitDomainWidth})`
  );

  const reportRef = useRef(null);

  useEffect(() => {
    if (!results || !formData) return;
    formData.candidate_names?.forEach((row) => {
      if (!selectedTrademarkRows.has(row)) {
        selectedTrademarkRows.add(row);
      }
    });
    formData.domain_name_candidates?.forEach((row) => {
      if (!selectedDomainRows.has(row)) {
        selectedDomainRows.add(row);
      }
    });
  }, [formData?.candidate_names, formData?.domain_name_candidates]);

  if (!results) return null;

  const { detailedReportData, reportSummaryData, domain_summary } = results;

  const getDataHandles = (formDataKey) => {
    const setSelectedRows =
      formDataKey === "candidate_names"
        ? setSelectedTrademarkRows
        : setSelectedDomainRows;
    const selectedRows =
      formDataKey === "candidate_names"
        ? selectedTrademarkRows
        : selectedDomainRows;
    return { selectedRows, setSelectedRows };
  };

  const handleRowSelect = (formDataKey, rowId) => {
    const { setSelectedRows } = getDataHandles(formDataKey);
    setSelectedRows((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(rowId)) {
        newSelected.delete(rowId);
        handleAddToCandidates(formDataKey, rowId, "remove");
      } else {
        newSelected.add(rowId);
        handleAddToCandidates(formDataKey, rowId, "add");
      }
      return newSelected;
    });
  };

  const handleSelectAll = (rows, idField, formDataKey) => {
    if (!rows || !Array.isArray(rows)) return;
    const { selectedRows, setSelectedRows } = getDataHandles(formDataKey);

    const allSelected = rows.every((row) => selectedRows.has(row[idField]));
    setSelectedRows((prev) => {
      const newSelected = new Set(prev);
      if (allSelected) {
        // If all are selected, deselect all
        rows.forEach((row) => newSelected.delete(row[idField]));
        handleAddAllCandidates(
          formDataKey,
          rows.map((row) => row[idField]),
          "remove"
        );
      } else {
        // If not all are selected, select all
        rows.forEach((row) => newSelected.add(row[idField]));
        handleAddAllCandidates(
          formDataKey,
          rows.map((row) => row[idField]),
          "add"
        );
      }
      return newSelected;
    });
  };

  const linkedReportColumnsWithCheckbox = [
    {
      id: "select",
      label: "Add to Input",
      width: "8%",
      format: (_, row) => (
        <div style={styles.checkboxContainer}>
          <Checkbox
            size="small"
            checked={selectedTrademarkRows.has(row["Name Candidate"])}
            onChange={() =>
              handleRowSelect("candidate_names", row["Name Candidate"])
            }
          />
        </div>
      ),
      headerFormat: () => {
        const { selectedRows } = getDataHandles("candidate_names");
        return (
          <div style={styles.checkboxHeaderContainer}>
            {!isMobileView && <span>Add to Input</span>}
            <Checkbox
              size="small"
              checked={
                linkedReport.length > 0 &&
                linkedReport.every((row) =>
                  selectedRows.has(row["Name Candidate"])
                )
              }
              indeterminate={
                linkedReport.some((row) =>
                  selectedRows.has(row["Name Candidate"])
                ) &&
                !linkedReport.every((row) =>
                  selectedRows.has(row["Name Candidate"])
                )
              }
              onChange={() =>
                handleSelectAll(
                  linkedReport,
                  "Name Candidate",
                  "candidate_names"
                )
              }
            />
          </div>
        );
      },
    },
    ...linkedReportColumns,
  ];

  const domainSummaryColumnsWithCheckbox = [
    {
      id: "select",
      label: "Add to Input",
      width: "8%",
      format: (_, row) => (
        <div style={styles.checkboxContainer}>
          <Checkbox
            size="small"
            checked={selectedDomainRows.has(row.name)}
            onChange={() => handleRowSelect("domain_name_candidates", row.name)}
          />
        </div>
      ),
      headerFormat: () => {
        const { selectedRows } = getDataHandles("domain_name_candidates");
        return (
          <div style={styles.checkboxHeaderContainer}>
            {!isMobileView && <span>Add to Input</span>}
            <Checkbox
              size="small"
              checked={
                formattedDomainSummary.length > 0 &&
                formattedDomainSummary.every((row) =>
                  selectedRows.has(row.name)
                )
              }
              indeterminate={
                formattedDomainSummary.some((row) =>
                  selectedRows.has(row.name)
                ) &&
                !formattedDomainSummary.every((row) =>
                  selectedRows.has(row.name)
                )
              }
              onChange={() =>
                handleSelectAll(
                  formattedDomainSummary,
                  "name",
                  "domain_name_candidates"
                )
              }
            />
          </div>
        );
      },
    },
    ...domainSummaryColumns,
  ];

  const formattedDomainSummary = (domain_summary || []).map((row) => {
    return {
      name: row.name,
      domain_availability_score: row["domain_availability_score"],
      min_price: row.min_price,
      max_price: row.max_price,
      available_domains: row.available_domains,
      exact_matches: row.exact_matches || "",
    };
  });

  const linkedReport = (reportSummaryData || []).map((row) => ({
    "Name Candidate": row.name,
    score: row.score || 0,
    "Name Similarity": row.name_similarity_score,
    "Industry Similarity": row.industry_similarity_score,
    "Legal Activity": row.legal_activity_score,
    "Existing Trademarks": Object.entries(row.mark_identification || {}).map(
      ([name, serial]) =>
        `[${name}] (https://tsdr.uspto.gov/#caseNumber=${serial}&caseSearchType=US_APPLICATION&caseType=DEFAULT&searchType=statusSearch)`
    ),
  }));

  const formattedDomainUtility = (results.domain_utility_score || []).map(
    (row) => ({
      name: row.name,
      exact_domain_name: row.exact_domain_name,
      seo_potential: row.seo_potential,
      clarity_readability: row.clarity_readability,
      brand_strength: row.brand_strength,
      functional_alignment: row.functional_alignment,
      brandability: row.brandability,
      notes: row.notes,
    })
  );

  const formattedBrandNameScores = (results.brand_name_scores || []).map(
    (row) => ({
      name: row.name,
      brand_appeal: row.brand_appeal,
      memorability: row.memorability,
      relevance: row.relevance,
      uniqueness: row.uniqueness,
      seo_searchability: row.seo_searchability,
      emotional_resonance: row.emotional_resonance,
      vibe: row.vibe,
    })
  );

  const formattedFullReport = (results.full_report_summary || []).map(
    (row) => ({
      name: row.name,
      mean_brand_score: row.mean_brand_score,
      trademark_score: row.trademark_score,
      domain_score: row.domain_score,
      exact_domain_count: row.exact_domain_count,
      vibe: row.vibe,
    })
  );

  const trademarkReportSummary = (
    <TrademarkReportSummary
      linkedReport={linkedReport}
      linkedReportColumns={linkedReportColumnsWithCheckbox}
      getDetailedRowColor={getDetailedRowColor}
      onCopyNames={onCopyNames}
      isMobile={isMobile}
      flipColumns={flipTrademarkReportCols}
      showSummaryReport={showSummaryReport}
      expandAccordian={trademarkReportExpanded}
      onAccordionChange={(event, expanded) =>
        setTrademarkReportExpanded(expanded)
      }
    />
  );

  const detailedTrademarkReport = (
    <DetailedTrademarkReport
      showDetailedReport={showDetailedReport}
      detailedReportData={detailedReportData || []}
      detailedColumns={detailedColumns}
      getDetailedRowColor={getDetailedRowColor}
      isMobile={isMobile}
      flipColumns={flipTrademarkReportCols}
    />
  );

  const domainNameReportSummary = (
    <DomainNameReportSummary
      formattedDomainSummary={formattedDomainSummary}
      domainSummaryColumns={domainSummaryColumnsWithCheckbox}
      getDetailedRowColor={getDetailedRowColor}
      expandAccordian={domainReportExpanded}
      onAccordionChange={(event, expanded) => setDomainReportExpanded(expanded)}
      isMobile={isMobile}
      showDomainReport={showDomainReport}
      flipColumns={flipDomainSummaryCols}
    />
  );

  const domainUtilityReport = (
    <DomainUtilityReport
      domainUtilityData={formattedDomainUtility}
      getDetailedRowColor={getDetailedRowColor}
      expandAccordian={domainUtilityExpanded}
      onAccordionChange={(event, expanded) =>
        setDomainUtilityExpanded(expanded)
      }
      isMobile={isMobile}
      flipColumns={flipDomainUtilityCols}
      showUtilityReport={showUtilityReport}
    />
  );

  const brandNameScoresReport = (
    <BrandNameScoresReport
      brandNameScoresData={formattedBrandNameScores}
      getDetailedRowColor={getDetailedRowColor}
      expandAccordian={brandNameScoresExpanded}
      onAccordionChange={(event, expanded) =>
        setBrandNameScoresExpanded(expanded)
      }
      isMobile={isMobile}
      flipColumns={flipBrandNameCols}
      showBrandNameScoresReport={showBrandNameScoresReport}
      selectedRows={selectedTrademarkRows}
      handleRowSelect={(name) => handleRowSelect("candidate_names", name)}
      handleSelectAll={() =>
        handleSelectAll(formattedBrandNameScores, "name", "candidate_names")
      }
    />
  );

  const fullReportSummary = (
    <FullReportSummary
      fullReportData={formattedFullReport}
      getDetailedRowColor={getDetailedRowColor}
      expandAccordian={fullReportExpanded}
      onAccordionChange={(event, expanded) => setFullReportExpanded(expanded)}
      isMobile={isMobile}
      flipColumns={flipFullReportCols}
      showFullReport={showFullReport}
      selectedRows={selectedTrademarkRows}
      handleRowSelect={(name) => handleRowSelect("candidate_names", name)}
      handleSelectAll={() =>
        handleSelectAll(formattedFullReport, "name", "candidate_names")
      }
    />
  );

  const socialMediaReport = (
    <SocialMediaUserNameAvailability
      brandNameScoresData={formattedBrandNameScores}
      expandAccordian={true}
      onAccordionChange={() => {}}
      isMobile={isMobile}
    />
  );

  const renderFormContent = () => {
    switch (jobType) {
      case "name_ideation":
        return (
          <>
            {brandNameScoresReport}
            {/* {socialMediaReport} */}
          </>
        );
        return <>{brandNameScoresReport}</>;
      case "trademark":
        return (
          <>
            {trademarkReportSummary}
            {detailedTrademarkReport}
          </>
        );
      case "domain":
        return (
          <>
            {domainNameReportSummary}
            {domainUtilityReport}
            <div style={styles.footnote}>
              *Only showing candidates with available domains.
            </div>
          </>
        );
      default:
        return (
          <>
            {fullReportSummary}
            {brandNameScoresReport}
            {trademarkReportSummary}
            {domainNameReportSummary}
            {domainUtilityReport}
            <div style={styles.footnote}>
              Only showing candidates with available domains.
            </div>
            {detailedTrademarkReport}
          </>
        );
    }
  };

  const handlePrint = () => {
    setDrawerOpen(false);
    setMobileMenuAnchorEl(null);
    // Store original states
    const originalStates = {
      domainReportExpanded,
      domainUtilityExpanded,
      brandNameScoresExpanded,
      trademarkReportExpanded,
      fullReportExpanded,
    };

    // Force expand all sections
    setDomainReportExpanded(true);
    setDomainUtilityExpanded(true);
    setBrandNameScoresExpanded(true);
    setTrademarkReportExpanded(true);
    setFullReportExpanded(true);

    // Wait for state updates to take effect
    setTimeout(() => {
      // Create a temporary div to hold the print content
      const printDiv = document.createElement("div");
      printDiv.style.position = "absolute";
      printDiv.style.left = "-9999px";
      printDiv.style.top = "-9999px";

      // Clone the content we want to print
      const contentToPrint = reportRef.current.cloneNode(true);

      // Force expand all accordions in the cloned content
      const accordions = contentToPrint.querySelectorAll(".MuiAccordion-root");
      accordions.forEach((accordion) => {
        // Force expanded state
        accordion.classList.add("Mui-expanded");

        // Ensure details are visible
        const details = accordion.querySelector(".MuiAccordionDetails-root");
        if (details) {
          details.style.display = "block";
          details.style.height = "auto";
          details.style.visibility = "visible";
          details.style.opacity = "1";
        }

        // Ensure collapse is expanded
        const collapse = accordion.querySelector(".MuiCollapse-root");
        if (collapse) {
          collapse.style.display = "block";
          collapse.style.height = "auto";
          collapse.style.visibility = "visible";
          collapse.style.opacity = "1";
        }

        // Ensure content is visible
        const content = accordion.querySelector(".MuiCollapse-wrapper");
        if (content) {
          content.style.display = "block";
          content.style.height = "auto";
          content.style.visibility = "visible";
          content.style.opacity = "1";
        }

        // Ensure inner content is visible
        const innerContent = accordion.querySelector(
          ".MuiCollapse-wrapperInner"
        );
        if (innerContent) {
          innerContent.style.display = "block";
          innerContent.style.height = "auto";
          innerContent.style.visibility = "visible";
          innerContent.style.opacity = "1";
        }
      });

      // Add the cloned content to the temporary div
      printDiv.appendChild(contentToPrint);
      document.body.appendChild(printDiv);

      // Get all styles
      const styles = Array.from(document.styleSheets)
        .map((styleSheet) => {
          try {
            return Array.from(styleSheet.cssRules)
              .map((rule) => rule.cssText)
              .join("\n");
          } catch (e) {
            return "";
          }
        })
        .join("\n");

      // Create print styles
      const printStyles = `
        @media print {
          body { margin: 0; padding: 20px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { padding: 8px; border: 1px solid #ddd; }
          .MuiTableCell-root { padding: 8px; }
          .MuiTableRow-root { page-break-inside: avoid; }
          .MuiAccordion-root { break-inside: avoid; }
          @page { margin: 1cm; }
          * { -webkit-print-color-adjust: exact !important; }
          * { print-color-adjust: exact !important; }
          .color-legend { display: block !important; }
          .color-legend-item { display: inline-block !important; margin-right: 10px !important; }
          .MuiCollapse-root { display: block !important; height: auto !important; visibility: visible !important; opacity: 1 !important; }
          .MuiCollapse-hidden { display: block !important; height: auto !important; visibility: visible !important; opacity: 1 !important; }
          .MuiAccordionDetails-root { display: block !important; visibility: visible !important; opacity: 1 !important; }
          .MuiAccordionSummary-expandIconWrapper { display: none !important; }
          .MuiTable-root { display: table !important; width: 100% !important; }
          .MuiTableHead-root { display: table-header-group !important; }
          .MuiTableBody-root { display: table-row-group !important; }
          .MuiTableRow-root { display: table-row !important; }
          .MuiTableCell-root { display: table-cell !important; }
          .MuiTableContainer-root { display: block !important; width: 100% !important; }
          .MuiCollapse-wrapper { display: block !important; height: auto !important; visibility: visible !important; opacity: 1 !important; }
          .MuiCollapse-wrapperInner { display: block !important; height: auto !important; visibility: visible !important; opacity: 1 !important; }
        }
      `;

      // Create a style element
      const styleElement = document.createElement("style");
      styleElement.textContent = styles + printStyles;
      printDiv.appendChild(styleElement);

      // Print
      window.print();

      // Clean up
      document.body.removeChild(printDiv);

      // Restore original states
      setDomainReportExpanded(originalStates.domainReportExpanded);
      setDomainUtilityExpanded(originalStates.domainUtilityExpanded);
      setBrandNameScoresExpanded(originalStates.brandNameScoresExpanded);
      setTrademarkReportExpanded(originalStates.trademarkReportExpanded);
      setFullReportExpanded(originalStates.fullReportExpanded);
    }, 100);
  };

  return (
    <div style={styles.container}>
      <Button
        variant="contained"
        startIcon={<PrintIcon />}
        onClick={handlePrint}
        sx={{ ...inputFormStyles.submitButton, mb: 3 }}
      >
        Print Report
      </Button>
      <div ref={reportRef}>{renderFormContent()}</div>
    </div>
  );
};

export default ResultsTables;
